import { useEffect, useRef, useState, useCallback, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, setUnauthorizedHandler } from './api.js';
import { withNamespaces } from './i18n.js';
import { lazyPage } from './lib/lazyPage.js';
import { record } from './lib/activity.js';
import { EVENT, PORTAL_PATHS } from '@essential-support/shared/constants';
import { applyBranding, rememberBranding } from './lib/format.js';
import { applyWorkspaceIcons } from './lib/appIcons.js';
import { applyTheme, getStoredTheme } from './lib/theme.js';
import { applyAccountLocale, getStoredLocale } from './lib/locale.js';
import { setBaseTitle } from './lib/docTitle.js';
import './pages/SignIn.css'; // SignIn now renders on the Astro /login/ page; its CSS still styles the boot splash, BootError, 2FA prompt, enrollment gate, and signup/forgot pages
import BootError from './components/BootError.jsx';
import UpdatePrompt from './components/UpdatePrompt.jsx';
import { isTenantlessHost } from './lib/host.js';
import { onActivated, useActivated } from './lib/prerender.js';
import { afterViewTransition } from './lib/viewTransition.js';
import * as bootCache from './lib/bootCache.js';
import { AuthContext } from './lib/authContext.js';

// The auth context lives in lib/authContext.js (so the KB header island can provide it
// without importing this module); every consumer imports useAuth from there.

// Every lazy view reloads once on a chunk that no longer exists (a deploy since this shell
// loaded) — lib/lazyPage.js, one session latch shared with the pages' own lazy views.

// Route-level code splitting. A session renders exactly ONE of these four roots, yet
// all four used to sit in the entry bundle — so a customer downloaded the agent console
// (MiniSearch + the whole filter/search rig), the owner settings and the platform admin
// console before their own ticket list could paint. SignIn stays statically imported on
// purpose: it is the latency-sensitive first paint for every signed-out visitor, it is
// small, and its stylesheet (SignIn.css) is where the boot splash below is defined —
// deferring it would leave the splash unstyled while a page chunk is in flight.
const SignupPage = lazyPage(() => import('./pages/SignupPage.jsx'));
const ForgotWorkspacesPage = lazyPage(() => import('./pages/ForgotWorkspacesPage.jsx'));
const PlatformApp = lazyPage(() => import('./platform/PlatformApp.jsx'));
const SandboxApp = lazyPage(() => import('./sandbox/SandboxApp.jsx'));
// One import() per page, shared with preloadPage below. Never `kind === 'agent' ? import(A)
// : import(B)`: the build wraps a ternary of dynamic imports in ONE preload carrying only
// the customer chunk's stylesheet, so a cached agent boot rendered without AgentPage.css
// (the notifications panel, the search token menu).
const importAgentPage = () => import('./pages/AgentPage.jsx');
const importCustomerPage = () => import('./pages/CustomerPage.jsx');
const AgentPage = lazyPage(importAgentPage);
const CustomerPage = lazyPage(importCustomerPage);
// The two sign-in gates are lazy too: only an agent mid-2FA or walled into enrolment ever sees
// one, and they'd otherwise carry the passkey library (and the enrolment gate its Security
// section + the `settings` catalog, which i18n.js loads on demand) into the entry.
const TwoFactorPrompt = lazyPage(() => import('./components/TwoFactorPrompt.jsx'));
const MfaEnrollmentGate = lazyPage(() => withNamespaces(import('./components/MfaEnrollmentGate.jsx'), ['settings']));

// A first render from the local cache (lib/bootCache.js, docs/KB.md gotcha 25) must not go
// through a Suspense fallback: React commits the fallback, then holds the content back for
// its reveal throttle (~300 ms) — the very spinner the cache is there to remove. So main.jsx
// preloads the cached viewer's page chunk (from the SW precache: a few ms) and renders once
// it's in; App then uses the module directly. A cold boot keeps the lazy components.
const loadedPages = { agent: null, customer: null };
export function preloadPage(kind) {
  const load = kind === 'agent' ? importAgentPage() : importCustomerPage();
  return load.then((m) => { loadedPages[kind] = m.default; });
}

// Everything /api/me decides about the document itself: brand colors + icons, the account
// theme/language (only when this device hasn't chosen its own), the title. Also run for the
// CACHED /api/me before a cached first render (main.jsx) — `remember` is off there (nothing
// new to remember). Returns the language switch's promise when it started one.
export function applyAccountChrome(me, { remember = true } = {}) {
  applyBranding(me.workspace?.branding);
  if (remember && me.workspace?.branding) rememberBranding(me.workspace.branding);
  applyWorkspaceIcons(me.workspace?.branding);
  // Account theme applies only when this device hasn't chosen one locally —
  // agents and customers both sync one when "apply to all my devices" is on.
  if (!getStoredTheme()) {
    const accountTheme = me.agent?.prefs?.theme || me.endUser?.prefs?.theme;
    if (accountTheme) applyTheme(accountTheme);
  }
  // Same precedence for language: the account preference (agent pref / customer
  // language) applies only when this device hasn't set one.
  let locale = null;
  if (!getStoredLocale()) {
    const accountLocale = me.agent?.prefs?.locale || me.endUser?.language;
    if (accountLocale) locale = applyAccountLocale(accountLocale);
  }
  // The base under any per-view title (lib/docTitle.js — the open ticket's subject, …).
  if (me.workspace?.name) setBaseTitle(me.workspace.name + ' · Support');
  return locale;
}

// The auth bootstrap and an in-flight page chunk render the SAME splash, so the hand-off
// from "checking the session" to "fetching the page" doesn't change a pixel — no flash
// of different content, no layout shift, just the spinner that was already turning.
//
// Arriving on the portal FROM another page of this site (a KB page's Tickets / bell / gear
// link — a same-origin referrer) the splash also draws the header band: the real header's
// classes, geometry and (cached) brand color, its contents left out. That's the frame a
// cross-document view transition fades into when the portal wasn't prerendered (Safari, or
// a click before the prerender finished) — the KB header then cross-fades in place into the
// band instead of into an empty page, and the real header fills it once /api/me lands
// (docs/KB.md gotcha 24). main.jsx makes it the document's FIRST frame (render-blocking
// entry + a synchronous first render). Cold loads (no same-origin referrer) keep the plain
// spinner: their header's shape (search row or not) isn't known yet.
const BOOT_BAND = (() => {
  try {
    // Any portal route (the SPA shell only ever loads on the tenant host's portal paths).
    return !isTenantlessHost() && new window.URL(document.referrer).origin === window.location.origin;
  } catch { return false; } // no referrer
})();
function BootSplash({ plain = false }) {
  // `plain`: a cached first render's fallback (a page chunk slower than main.jsx waited for)
  // — no band: the page that replaces it brings the real header (a view transition must
  // never see its named header element swapped mid-fade — lib/viewTransition.js).
  if (!BOOT_BAND || plain) return <div className="boot-splash"><div className="spinner" /></div>;
  return (
    <div className="boot-shell">
      <div className="es-header es-header--search es-header-boot" aria-hidden="true">
        <div className="es-container">
          <div className="es-header-top"><div className="es-header-actions"><span className="es-icon-btn kb-nav-btn" /></div></div>
          <div className="es-header-search"><div className="es-search"><span className="es-search-trailing" /></div></div>
        </div>
      </div>
      <div className="boot-splash"><div className="spinner" /></div>
    </div>
  );
}

export default function App() {
  // A returning viewer starts from the cached /api/me chrome (`cached` until the real one
  // lands); everyone else from the boot splash.
  const [state, setState] = useState(() => (bootCache.boot
    ? { loading: false, cached: true, ...bootCache.boot.me }
    : { loading: true, workspace: null, agent: null, endUser: null }));
  // The page roots this document renders: the preloaded modules when the first render came
  // from the cache (main.jsx), else the lazy components. Fixed at mount — switching between
  // the two would remount the page.
  const [Pages] = useState(() => ({ agent: loadedPages.agent || AgentPage, customer: loadedPages.customer || CustomerPage, plain: !!bootCache.boot }));
  const navigate = useNavigate();
  const location = useLocation();
  // false while this document is a speculation-rules prerender (lib/prerender.js).
  const activated = useActivated();

  // The first /api/me result replaces the boot frame (the band's .es-header element) — only
  // after a cross-document view transition into this page has finished (lib/viewTransition.js:
  // replacing the named element mid-fade aborts it). Later refreshes don't wait.
  const booted = useRef(false);
  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      if (!booted.current) { booted.current = true; await afterViewTransition(); }
      // The local copy follows the live answer: kept for the same viewer, discarded for
      // anyone else (the page below is keyed by subject, so it remounts clean), purged for
      // a signed-out / mid-2FA / enrolment-walled result.
      bootCache.confirm(me);
      applyAccountChrome(me);
      setState({ loading: false, ...me });
    } catch (err) {
      // Offline (or /api/me failing) after a cached first render: keep showing the last-seen
      // state rather than an error page — unless that copy was purged meanwhile (a 401).
      setState((s) => (s.cached && bootCache.isLive() ? s
        : { loading: false, workspace: null, agent: null, endUser: null, error: err.message }));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // If a request 401s (e.g. this device was evicted by a sign-in elsewhere), drop the
  // now-dead socket and re-validate — which lands on the sign-in screen if logged out.
  // The socket module (and socket.io-client) lives in the page chunks, not the entry, so it's
  // imported here — already loaded: a socket only ever opens on a tenant host, from a page
  // chunk, which is also what made the request that 401'd.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      bootCache.purge();
      if (!isTenantlessHost()) import('./lib/socket.js').then((m) => m.disconnectSocket(), () => {});
      refresh();
    });
    return () => setUnauthorizedHandler(null);
  }, [refresh]);

  // Usage beacon: one page.viewed per route. Normalize id segments so routes stay
  // low-cardinality (/tickets/<objectid> → /tickets/:id). No-op until activity.init
  // runs (main.jsx), so it never fires on the signup or admin hosts.
  useEffect(() => {
    if (state.platformAdmin?.enabled) return; // no usage beacon on the admin console
    const route = location.pathname.replace(/\/[0-9a-fA-F]{24}(?=\/|$)/g, '/:id');
    record(EVENT.PAGE_VIEWED, { route });
  }, [location.pathname, state.platformAdmin]);

  // Signed out on a TENANT portal host: the sign-in page is the Astro-served /login/
  // now — never rendered inside the SPA — so bounce there (full-page). Gated on the
  // BUILD-TIME host (isTenantlessHost), so a signup/admin/sandbox host whose /api/me
  // returned no flags can never loop into /login/. Requires a clean signed-out result
  // (!state.error); an /api/me failure falls to BootError below instead. Declared before
  // the loading early-return to keep hook order stable.
  const signedOut = !state.loading && !state.error && !state.signup?.enabled
    && !state.platformAdmin?.enabled && !state.sandbox?.enabled && !state.mfaPending
    && !state.agent && !state.endUser;
  // A prerendered portal redirects only once it's shown (a hidden page must not navigate).
  useEffect(() => {
    if (!signedOut || isTenantlessHost()) return undefined;
    return onActivated(() => window.location.replace(PORTAL_PATHS.login));
  }, [signedOut]);

  // An AI app's pending connection (docs/kb-specs/P3-SPEC.md §4.3): /oauth/authorize leaves
  // a 10-minute cookie, and /api/me reports it to a signed-in owner as `connect.requestId`
  // — so an owner who had to sign in first (the magic link carries no `next`) lands on the
  // consent page anyway. Once per request id: leaving the page must not bounce them back.
  const connectRequestId = state.agent ? state.connect?.requestId || null : null;
  const routedConnect = useRef(null);
  useEffect(() => {
    if (!connectRequestId || routedConnect.current === connectRequestId) return;
    routedConnect.current = connectRequestId;
    if (/^\/tickets\/connect(?:\/|$)/.test(location.pathname)) return;
    navigate(`${PORTAL_PATHS.connect}?request=${encodeURIComponent(connectRequestId)}`);
  }, [connectRequestId, location.pathname, navigate]);

  const value = { ...state, refresh, navigate, location };

  if (state.loading) {
    return <BootSplash />;
  }

  // Host-based branching is unchanged — /api/me still decides which root renders; only
  // the module for that root now arrives on demand instead of up front.
  let page;
  if (state.signup?.enabled) {
    // /forgot on the signup host is the self-serve workspace-recall page; everything
    // else on this host is the signup form.
    page = location.pathname.startsWith('/forgot')
      ? <ForgotWorkspacesPage signup={state.signup} />
      : <SignupPage signup={state.signup} />;
  }
  else if (state.platformAdmin?.enabled) page = <PlatformApp platform={state.platformAdmin} onRefresh={refresh} />;
  else if (state.sandbox?.enabled) page = <SandboxApp sandbox={state.sandbox} onRefresh={refresh} />;
  // 2FA gate — an agent who confirmed the magic link but hasn't cleared the second
  // factor. MUST come before agent/endUser: they hold no agent session yet, and an
  // agent who also has a portal (endUser) cookie must still see the 2FA prompt, not
  // the customer portal.
  else if (state.mfaPending) page = <TwoFactorPrompt pending={state.mfaPending} onVerified={refresh} />;
  // Signed in, but the workspace requires 2FA and this agent hasn't enrolled — wall
  // them into enrollment (the server enforces it too). Must precede the console.
  else if (state.agent && state.mustEnrollPasskey) page = <MfaEnrollmentGate passkeyOnly />;
  else if (state.agent && state.mustEnrollMfa) page = <MfaEnrollmentGate />;
  // Keyed by subject: a different viewer than the cached one remounts the page from scratch.
  else if (state.agent) page = <Pages.agent key={`a:${state.agent.id}`} />;
  else if (state.endUser) page = <Pages.customer key={`c:${state.endUser.id}`} />;
  // Signed out. On a tenant host the effect above is redirecting to the Astro /login/
  // page — show the splash meanwhile. On an /api/me error, or on a tenantless host that
  // fell through here (its flags never resolved), offer a retry instead of a dead page.
  else if (state.error || isTenantlessHost()) page = <BootError onRetry={refresh} />;
  else page = <BootSplash />;

  // The PWA updater registers the service worker + owns the update flow. Gate it on the
  // BUILD-TIME host, never /api/me state: the signup/admin hosts are SW-free, and a failed
  // /api/me (all flags undefined) must NOT be mistaken for a portal and register an SW
  // there (Fable finding). isTenantlessHost() is the same check main.jsx registers by.
  // Not in a prerender either: registering, polling for and APPLYING an update (a
  // skipWaiting + reload across every tab) waits until the page is actually shown — its
  // "arrived onto a stale shell" clock then starts at activation.
  const mountUpdater = !isTenantlessHost() && activated;

  // The provider sits OUTSIDE the boundary so the page chunk finds its auth context
  // already populated the moment it mounts — no second render pass, no null-context
  // window. SignIn never suspends, so it renders straight through.
  return (
    <AuthContext.Provider value={value}>
      <Suspense fallback={<BootSplash plain={Pages.plain} />}>{page}</Suspense>
      {mountUpdater && <UpdatePrompt />}
    </AuthContext.Provider>
  );
}
